import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { MapPin, CalendarDays, Bell, Users, MailQuestion, AlertCircle, ChevronRight } from 'lucide-react';

const fmtDate = (d) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${day}.${m}.${y}.`; };

export default function AdminDashboard() {
  const { user, hasPermission } = useAuth();
  const [data, setData] = useState({ offers: [], events: [], notices: [], users: [], inquiries: [], changeRequests: [] });

  useEffect(() => {
    const load = async () => {
      const results = await Promise.allSettled([
        api.get('/offers/all'), api.get('/events/all'), api.get('/notices'),
        api.get('/users'), api.get('/inquiries'), api.get('/change-requests')
      ]);
      const val = (r) => (r.status === 'fulfilled' ? r.value.data : []);
      setData({
        offers: val(results[0]), events: val(results[1]), notices: val(results[2]),
        users: val(results[3]), inquiries: val(results[4]), changeRequests: val(results[5])
      });
    };
    load();
    window.addEventListener('store-updated', load);
    return () => window.removeEventListener('store-updated', load);
  }, []);

  const pendingUsers = data.users.filter((u) => u.status === 'pending');
  const pendingEvents = data.events.filter((e) => e.status === 'pending');
  const pendingRequests = data.changeRequests.filter((r) => r.status === 'pending');

  const stats = [
    { label: 'Turistička ponuda', value: data.offers.length, icon: MapPin, to: '/admin/ponuda' },
    { label: 'Događaji', value: data.events.length, icon: CalendarDays, to: '/admin/dogadjaji' },
    { label: 'Obavijesti', value: data.notices.length, icon: Bell, to: '/admin/obavijesti' },
    { label: 'Korisnici sistema', value: data.users.length, icon: Users, to: '/admin/korisnici' }
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-[#0b1a3d] mb-1">Kontrolna ploča</h1>
      <p className="text-sm text-slate-500 mb-8">Dobrodošli, {user?.name}. Pregled stanja sistema E-Turizam.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s, i) => (
          <Link to={s.to} key={i} data-testid={`stat-card-${i}`}
            className="bg-white rounded-xl border border-slate-100 p-5 hover:shadow-lg hover:shadow-slate-200/60 transition-shadow duration-300">
            <div className="w-10 h-10 rounded-lg bg-[#e8ecf7] flex items-center justify-center mb-3">
              <s.icon className="h-5 w-5 text-[#001752]" />
            </div>
            <p className="text-3xl font-bold text-[#001752]">{s.value}</p>
            <p className="text-sm text-slate-500 mt-0.5">{s.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ZAHTJEVI NA ČEKANJU */}
        {(hasPermission('approve_subjects') || hasPermission('approve_events')) && (
          <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="pending-box">
            <h2 className="font-bold text-[#0b1a3d] mb-4 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" /> Zahtjevi na čekanju
            </h2>
            <div className="space-y-3">
              {pendingUsers.map((u) => (
                <Link key={u.id} to="/admin/korisnici" className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg p-3 hover:bg-amber-100/60 transition-colors duration-200">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Registracija subjekta: {u.name}</p>
                    <p className="text-xs text-slate-500">{u.activityCategory} • {u.city}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </Link>
              ))}
              {pendingEvents.map((e) => (
                <Link key={e.id} to="/admin/dogadjaji" className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg p-3 hover:bg-amber-100/60 transition-colors duration-200">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Događaj: {e.title}</p>
                    <p className="text-xs text-slate-500">{fmtDate(e.date)} • {e.city}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </Link>
              ))}
              {pendingRequests.map((r) => (
                <Link key={r.id} to="/admin/korisnici" className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg p-3 hover:bg-amber-100/60 transition-colors duration-200">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Izmjena podataka subjekta</p>
                    <p className="text-xs text-slate-500">{r.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </Link>
              ))}
              {pendingUsers.length + pendingEvents.length + pendingRequests.length === 0 && (
                <p className="text-sm text-slate-500">Nema zahtjeva na čekanju.</p>
              )}
            </div>
          </div>
        )}

        {/* UPITI */}
        <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="inquiries-box">
          <h2 className="font-bold text-[#0b1a3d] mb-4 flex items-center gap-2">
            <MailQuestion className="h-4 w-4 text-[#001752]" /> Zaprimljeni upiti ({data.inquiries.length})
          </h2>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {data.inquiries.length === 0 && <p className="text-sm text-slate-500">Još nema zaprimljenih upita putem kontakt forme.</p>}
            {data.inquiries.map((q) => (
              <div key={q.id} className="border border-slate-100 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold text-slate-700">{q.name} <span className="text-slate-400 font-normal">({q.email})</span></p>
                  <span className="text-xs text-slate-400">{fmtDate(q.date)}</span>
                </div>
                {q.offerTitle && <p className="text-xs text-[#001752] font-medium mb-1">Upit za: {q.offerTitle}</p>}
                {q.title && <p className="text-xs text-[#001752] font-medium mb-1">{q.title}</p>}
                <p className="text-xs text-slate-500 line-clamp-2">{q.message}</p>
              </div>
            ))}
          </div>
        </div>

        {/* AUDIT LOG (demo) */}
        <div className="bg-white rounded-xl border border-slate-100 p-6 lg:col-span-2" data-testid="audit-log">
          <h2 className="font-bold text-[#0b1a3d] mb-4">Evidencija aktivnosti (audit log)</h2>
          <div className="space-y-2 text-sm">
            {[
              { u: 'admin@eturizam.ba', a: 'Odobrena registracija subjekta „Hotel Blanca d.o.o.“', t: '28.07.2025. 14:22' },
              { u: 'tz@eturizam.ba', a: 'Dodan događaj „Plivska regata“', t: '27.07.2025. 10:05' },
              { u: 'ministarstvo@eturizam.ba', a: 'Objavljena obavijest „Javni poziv za registraciju smještajnih kapaciteta“', t: '21.07.2025. 09:14' },
              { u: 'opcina@eturizam.ba', a: 'Ažurirana turistička ponuda „Travnička tvrđava“', t: '18.07.2025. 13:47' }
            ].map((l, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-50 pb-2 last:border-0">
                <p className="text-slate-600"><span className="font-semibold text-[#001752]">{l.u}</span> – {l.a}</p>
                <span className="text-xs text-slate-400">{l.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
